import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { CONTENT_TYPES, versionsApi, previewApi, TYPE_TO_COLLECTION, TYPE_TO_PUBLIC_PATH, archiveApi, mediaExtraApi } from "./api";
import { seoApi } from "../site/api";
import ClinicalInsights from "./ClinicalInsights";
import Connections from "./Connections";
import { Breadcrumbs } from "../AdminLayout";
import { formatErr, tokenStore } from "../api";
import { toast, Toaster } from "sonner";
import { Save, Trash2, Eye, Send, X, ArrowLeft, History, Check, Archive, Unlock, ShieldCheck } from "lucide-react";

const inputCls = "w-full bg-white border border-line px-3 py-2 text-sm focus:outline-none focus:border-navy";

// Field definitions per content type (drives the editor)
const SCHEMAS = {
  articles: [
    ["slug","Slug","text",true],["title","Title","text",true],["subtitle","Subtitle","text",false],
    ["excerpt","Excerpt","textarea",false],["body_markdown","Body (markdown)","longtext",false],
    ["category","Category","text",false],["tags","Tags (comma)","tags",false],
    ["target_audience","Audience","select:patient|doctor|both",false],
    ["reading_time_minutes","Reading time (min)","number",false],
    ["reviewed_on","Reviewed on","date",false],
    ["published_at","Published at","date",false],
    ["scheduled_for","Scheduled for","datetime",false],
    ["linked_condition_slugs","Linked condition slugs (comma)","tags",false],
    ["featured_image","Featured image URL","text",false],
  ],
  conditions: [
    ["slug","Slug","text",true],["name","Name","text",true],["category","Category","text",true],
    ["severity","Severity","select:informational|monitor|urgent",false],
    ["overview","Overview","longtext",false],
    ["symptoms","Symptoms","list",false],
    ["investigations","Investigations","list",false],
    ["treatments","Treatments","list",false],
    ["guidelines","Related guideline codes","list",false],
    ["trimesters","Trimesters","tags",false],
    ["related_condition_slugs","Related condition slugs","tags",false],
    ["reviewed_on","Reviewed on","date",false],
    ["hero_image","Hero image URL","text",false],
  ],
  faqs: [
    ["question","Question","text",true],["answer_markdown","Answer (markdown)","longtext",true],
    ["category","Category","text",false],["order","Order","number",false],
    ["linked_article_slugs","Linked article slugs","tags",false],
  ],
  guidelines: [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["issuing_body","Issuing body","select:ACOG|FIGO|RCOG|NICE|ICOG|WHO|Other",true],
    ["year","Year","number",true],["version","Version","text",false],
    ["summary","Summary","longtext",false],["url","Official URL","text",false],
    ["key_recommendations","Key recommendations","list",false],
    ["covers_condition_slugs","Covers condition slugs","tags",false],
  ],
  "research-papers": [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["journal","Journal","text",false],["publication_date","Publication date","date",false],
    ["study_type","Study type","select:rct|meta_analysis|cohort|case_control|case_series|guideline|other",false],
    ["doi","DOI","text",false],["pubmed_id","PubMed ID","text",false],["url","URL","text",false],
    ["abstract_verbatim","Abstract (verbatim)","longtext",false],
    ["keywords","Keywords","tags",false],
    ["topic_condition_slugs","Topic condition slugs","tags",false],
    ["quality_tier","Quality tier","select:tier_1|tier_2|tier_3",false],
  ],
  "research-updates": [
    ["slug","Slug","text",true],["title","Title","text",true],["subtitle","Subtitle","text",false],
    ["linked_paper_ids","Linked paper IDs","tags",false],
    ["plain_language_summary_markdown","Plain-language summary","longtext",false],
    ["clinical_takeaways","Clinical takeaways","list",false],
    ["what_this_changes_markdown","What this changes (Dr. Tania's view)","longtext",false],
    ["linked_condition_slugs","Linked condition slugs","tags",false],
    ["reviewed_on","Reviewed on","date",false],
    ["published_at","Published at","date",false],
  ],
  "evidence-summaries": [
    ["slug","Slug","text",true],["title","Title","text",true],["subtitle","Subtitle","text",false],
    ["subject_scope","Subject scope","text",false],
    ["body_markdown","Body (markdown, long-form synthesis)","longtext",false],
    ["key_recommendations","Key recommendations","list",false],
    ["linked_paper_ids","Linked paper IDs","tags",false],
    ["linked_guideline_slugs","Linked guideline slugs","tags",false],
    ["linked_condition_slugs","Linked condition slugs","tags",false],
    ["reviewed_on","Reviewed on","date",false],
    ["last_updated_on","Last updated on","date",false],
  ],
  references: [
    ["type","Type","select:journal_article|guideline|book|website|government_report",true],
    ["title","Title","text",true],
    ["journal","Journal","text",false],["year","Year","number",false],
    ["volume","Volume","text",false],["issue","Issue","text",false],["pages","Pages","text",false],
    ["publisher","Publisher","text",false],["edition","Edition","text",false],
    ["doi","DOI","text",false],["pubmed_id","PubMed ID","text",false],["url","URL","text",false],
    ["quality_tier","Quality tier","select:tier_1|tier_2|tier_3",false],
  ],
  treatments: [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["category","Category","text",false],
    ["modality","Modality","select:medication|procedure|monitoring|lifestyle|counselling|other",false],
    ["overview","Overview","longtext",false],
    ["when_indicated","When indicated","longtext",false],
    ["typical_setting","Typical setting","text",false],
    ["considerations","Considerations","list",false],
    ["reviewed_on","Reviewed on","date",false],
  ],
  symptoms: [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["lay_description","Lay description","textarea",false],
    ["clinical_description","Clinical description","longtext",false],
    ["red_flag","Red flag symptom","checkbox",false],
    ["when_to_seek_help","When to seek help","textarea",false],
  ],
  investigations: [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["category","Category","select:laboratory|imaging|physiological|genetic|other",false],
    ["purpose","Purpose","textarea",false],
    ["typical_timing","Typical timing","text",false],
    ["preparation_notes","Preparation notes","textarea",false],
    ["reference_ranges","Reference ranges","textarea",false],
  ],
  medicines: [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["generic_name","Generic name","text",false],
    ["drug_class","Drug class","text",false],
    ["pregnancy_category_notes","Pregnancy category (narrative, informational only)","longtext",false],
    ["common_uses_informational","Common uses (informational)","longtext",false],
    ["key_cautions","Key cautions","list",false],
    ["is_prescription_only","Prescription only","checkbox",false],
  ],
  trimesters: [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["order","Order","number",false],
    ["week_range","Week range","text",false],
    ["overview","Overview","longtext",false],
    ["key_milestones","Key milestones","list",false],
  ],
  videos: [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["provider","Provider","select:youtube|vimeo|self_hosted|other",false],
    ["embed_url","Embed URL","text",true],
    ["thumbnail_url","Thumbnail URL","text",false],
    ["duration_seconds","Duration (seconds)","number",false],
    ["description_md","Description","longtext",false],
    ["transcript_md","Transcript","longtext",false],
    ["chapters","Chapters (JSON list of {time_seconds, label})","json",false],
    ["categories","Categories","tags",false],
    ["reference_ids","Reference IDs","tags",false],
  ],
  "patient-guides": [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["subtitle","Subtitle","text",false],
    ["description_md","Description","longtext",false],
    ["audience","Audience","select:patient|partner|clinician|student|general",false],
    ["language","Language","text",false],
    ["cover_image_url","Cover image URL","text",false],
    ["download_url","Download URL","text",true],
    ["file_size_kb","File size (KB)","number",false],
    ["page_count","Page count","number",false],
    ["reading_time_minutes","Reading time (min)","number",false],
    ["tags","Tags","tags",false],
    ["reference_ids","Reference IDs","tags",false],
  ],
  infographics: [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["description_md","Description","longtext",false],
    ["alt_text","Alt text (accessibility)","textarea",true],
    ["credit","Credit","text",false],
    ["master","Master (preserved · locked)","master",false],
    ["derivatives","Derivatives (JSON list)","json",false],
    ["tags","Tags","tags",false],
    ["reference_ids","Reference IDs","tags",false],
  ],
  books: [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["subtitle","Subtitle","text",false],
    ["authors","Authors","list",false],
    ["cover_image_url","Cover image URL","text",false],
    ["description_md","Description","longtext",false],
    ["isbn","ISBN","text",false],
    ["publisher","Publisher","text",false],
    ["published_year","Published year","number",false],
    ["page_count","Page count","number",false],
    ["language","Language","text",false],
    ["purchase_links","Purchase links (JSON list of {label, url, region})","json",false],
    ["chapters","Chapters (JSON list of {number, title, description})","json",false],
    ["tags","Tags","tags",false],
    ["reference_ids","Reference IDs","tags",false],
  ],
  testimonials: [
    ["slug","Slug","text",true],
    ["patient_alias","Patient alias (anonymized)","text",true],
    ["condition_context","Condition context","text",false],
    ["story_md","Story","longtext",true],
    ["consent_given","Consent recorded (signed)","checkbox",true],
    ["consent_recorded_at","Consent recorded at","date",false],
    ["consent_reference","Consent reference (form id / drive link)","text",false],
    ["avatar_url","Avatar URL (optional)","text",false],
    ["linked_condition_slugs","Linked condition slugs","tags",false],
    ["moderation","Moderation","moderation",false],
    ["tags","Tags","tags",false],
  ],
  "media-appearances": [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["outlet","Outlet","text",true],
    ["appearance_type","Type","select:article|interview|podcast|tv|panel|conference|other",false],
    ["published_date","Published date","date",false],
    ["url","URL","text",true],
    ["thumbnail_url","Thumbnail URL","text",false],
    ["duration_seconds","Duration (seconds)","number",false],
    ["description_md","Description","longtext",false],
    ["transcript_md","Transcript","longtext",false],
    ["presenters","Presenters","list",false],
    ["tags","Tags","tags",false],
    ["reference_ids","Reference IDs","tags",false],
  ],
  "teaching-slides": [
    ["slug","Slug","text",true],["title","Title","text",true],
    ["audience","Audience","select:students|residents|clinicians|public|mixed",false],
    ["deck_url","Deck URL","text",false],
    ["thumbnail_url","Thumbnail URL","text",false],
    ["description_md","Description","longtext",false],
    ["slides","Slides (JSON list of {number,title,notes_md})","json",false],
    ["reference_ids","Reference IDs","tags",false],
    ["tags","Tags","tags",false],
  ],
  "clinical-tools": [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["category","Category","select:calculator|checklist|screening|assessment|other",false],
    ["purpose","Purpose","longtext",false],
    ["inputs","Inputs (JSON list of {key,label,type,unit,options,required})","json",false],
    ["formula_notes","Formula notes (narrative only)","longtext",false],
    ["output_units","Output units","text",false],
    ["disclaimer","Disclaimer","longtext",false],
    ["reference_ids","Reference IDs","tags",false],
  ],
  "ai-prompts": [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["category","Category","select:summarisation|extraction|editing|review|translation|other",false],
    ["intended_model_slug","Intended model slug","text",false],
    ["prompt_template","Prompt template","longtext",true],
    ["input_variables","Input variables","tags",false],
    ["example_output","Example output","longtext",false],
    ["safety_notes","Safety notes","longtext",false],
    ["version","Version","text",false],
    ["tags","Tags","tags",false],
  ],
  "ai-models": [
    ["slug","Slug","text",true],["name","Name","text",true],
    ["provider","Provider","select:openai|anthropic|google|meta|mistral|other",false],
    ["version","Version","text",true],
    ["modality","Modality","select:text|image|audio|video|multimodal",false],
    ["capabilities","Capabilities","tags",false],
    ["context_window_tokens","Context window (tokens)","number",false],
    ["approved_for_clinical_use","Approved for clinical use","checkbox",false],
    ["approval_notes","Approval notes","longtext",false],
    ["added_by","Added by","text",false],
  ],
};

export default function ContentEditor() {
  const { type, id } = useParams();
  const cfg = CONTENT_TYPES[type];
  const nav = useNavigate();
  const isNew = id === "new";
  const [item, setItem] = useState(isNew ? initFromSchema(SCHEMAS[type]) : null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [autosaveTs, setAutosaveTs] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const schema = SCHEMAS[type] || [];
  const collName = TYPE_TO_COLLECTION[type];
  const autosaveTimer = useRef(null);

  useEffect(() => {
    if (!isNew && cfg) cfg.api.get(id).then(setItem).catch(()=>toast.error("Load failed"));
  }, [id, isNew, cfg]);

  const loadVersions = () => { if (!isNew && collName) versionsApi.list(collName, id).then(setVersions).catch(()=>{}); };
  useEffect(() => { loadVersions(); /* eslint-disable-next-line */ }, [id, isNew]);

  // Autosave: debounced 8s after last change (only on existing items). Must be above early returns.
  useEffect(() => {
    if (isNew || !dirty || !item) return;
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current);
    autosaveTimer.current = setTimeout(() => { save({}, true); }, 8000);
    return () => { if (autosaveTimer.current) clearTimeout(autosaveTimer.current); };
    // eslint-disable-next-line
  }, [item, dirty]);

  if (!cfg) return <div className="p-8">Unknown content type.</div>;
  if (!item) return <div className="p-8">Loading…</div>;

  const set = (k, v) => { setItem(s => ({ ...s, [k]: v })); setDirty(true); };

  const save = async (extra = {}, silent = false) => {
    setSaving(true);
    try {
      const payload = { ...item, ...extra };
      if (isNew) {
        const d = await cfg.api.create(payload);
        if (!silent) toast.success("Created");
        nav(`/admin/content/${type}/${d.id}`, { replace: true });
      } else {
        await cfg.api.update(id, payload);
        if (!silent) toast.success("Saved");
        const fresh = await cfg.api.get(id); setItem(fresh);
        loadVersions();
      }
      setDirty(false);
      setAutosaveTs(new Date());
    } catch (e) { if (!silent) toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const publish = () => save({ status: "published", published_at: new Date().toISOString() });
  const unpublish = () => save({ status: "unpublished" });
  const del = async () => {
    if (!window.confirm("Delete this item permanently?")) return;
    try { await cfg.api.delete(id); toast.success("Deleted"); nav(`/admin/content/${type}`); }
    catch (e) {
      const msg = formatErr(e.response?.data?.detail);
      toast.error(msg);
      if (typeof msg === "string" && msg.toLowerCase().includes("connected link")) {
        toast("Tip: use Archive instead — it preserves connections.", { duration: 6000 });
      }
    }
  };

  const archive = async () => {
    if (!window.confirm("Archive this item? It will be unpublished but connections remain intact.")) return;
    try { await archiveApi.archive(collName, id); toast.success("Archived"); const fresh = await cfg.api.get(id); setItem(fresh); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const unlockMaster = async () => {
    const reason = window.prompt("Reason for unlocking the master (audit-logged):", "Regenerated master asset");
    if (!reason) return;
    try {
      await mediaExtraApi.unlockMaster(id, reason);
      toast.success("Master unlocked. Update the master fields and Save. It will re-lock after save.");
      const fresh = await cfg.api.get(id); setItem(fresh);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const approveTestimonial = async () => {
    const notes = window.prompt("Approval notes (optional):", "");
    try {
      await mediaExtraApi.approveTestimonial(id, true, notes || "");
      toast.success("Testimonial approved for publication");
      const fresh = await cfg.api.get(id); setItem(fresh);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };
  const unapproveTestimonial = async () => {
    try {
      await mediaExtraApi.approveTestimonial(id, false, "");
      toast.success("Approval revoked");
      const fresh = await cfg.api.get(id); setItem(fresh);
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const openPreview = async () => {
    if (!collName || !TYPE_TO_PUBLIC_PATH[type]) { toast.error("Preview only available for Articles and Conditions"); return; }
    try {
      if (dirty) await save({}, true);
      const { token, slug } = await previewApi.issue(collName, id);
      window.open(`/${TYPE_TO_PUBLIC_PATH[type]}/${slug}?preview=${token}`, "_blank", "noopener");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const restore = async (vid) => {
    if (!window.confirm("Restore this version? Current content will be snapshotted first.")) return;
    try { await versionsApi.restore(collName, id, vid); toast.success("Restored"); const fresh = await cfg.api.get(id); setItem(fresh); loadVersions(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="content-editor">
      <Toaster richColors position="top-right"/>
      <Breadcrumbs items={["Admin", "Content", cfg.label, isNew ? "New" : (item.title || item.name || item.question || "Edit")]}/>
      <div className="flex items-center justify-between mb-4">
        <Link to={`/admin/content/${type}`} className="mono text-[10px] uppercase tracking-widest text-stone2 hover:text-navy inline-flex items-center gap-1"><ArrowLeft size={12}/> Back to list</Link>
        {!isNew && (
          <div className="mono text-[10px] uppercase tracking-widest text-stone2">
            {saving ? "Saving…" : dirty ? "Unsaved changes" : autosaveTs ? `Autosaved ${autosaveTs.toLocaleTimeString()}` : "All changes saved"}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 space-y-6">
          <div className="bg-white border border-line p-6">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">Content</div>
            <div className="mt-4 space-y-4">
              {schema.map(([k, label, kind, required]) => (
                <FieldRow key={k} k={k} label={label} kind={kind} required={required} value={item[k]} onChange={v => set(k, v)}/>
              ))}
            </div>
          </div>
          {cfg.hasInsights && !isNew && <ClinicalInsights host={cfg.host} hostId={id}/>}
          {cfg.hasConnections && !isNew && collName && (
            <Connections entityType={collName} entityId={id} currentUserRole={tokenStore.user?.role}/>
          )}
          {type === "medicines" && (
            <div data-testid="medicine-info-notice" className="border border-[#D97706]/30 bg-[#D97706]/5 px-4 py-3 text-xs text-[#8B5A00]">
              Medicines are informational only. Do not add dosing, regimens, or prescribing instructions. Clinical prescription decisions must remain with the treating clinician.
            </div>
          )}
        </div>

        <aside className="lg:col-span-4 space-y-4">
          <div className="bg-white border border-line p-5">
            <div className="mono text-[10px] uppercase tracking-widest text-prestige">Publish</div>
            <div className="mt-3"><span data-testid="publish-status" className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${statusChip(item.status)}`}>{(item.status||"draft").replace('_',' ')}</span></div>
            <div className="mt-4 space-y-2">
              <button data-testid="btn-save" disabled={saving} onClick={()=>save()} className="w-full bg-navy hover:bg-navy-hover text-white text-sm py-2 flex items-center justify-center gap-2 disabled:opacity-60"><Save size={14}/> {saving ? "Saving…" : "Save"}</button>
              {!isNew && (item.status !== "published" ? (
                <button data-testid="btn-publish" onClick={publish} className="w-full bg-prestige hover:bg-prestige/85 text-white text-sm py-2 flex items-center justify-center gap-2"><Send size={14}/> Publish</button>
              ) : (
                <button data-testid="btn-unpublish" onClick={unpublish} className="w-full border border-line hover:border-navy text-navy text-sm py-2 flex items-center justify-center gap-2"><X size={14}/> Unpublish</button>
              ))}
              {!isNew && TYPE_TO_PUBLIC_PATH[type] && (
                <button data-testid="btn-live-preview" onClick={openPreview} className="w-full border border-navy text-navy hover:bg-navy hover:text-white text-sm py-2 flex items-center justify-center gap-2"><Eye size={14}/> Open live preview</button>
              )}
              {!isNew && cfg.hasConnections && (
                <button data-testid="btn-archive" onClick={archive} className="w-full border border-[#D97706] text-[#D97706] hover:bg-[#D97706] hover:text-white text-sm py-2 flex items-center justify-center gap-2"><Archive size={14}/> Archive</button>
              )}
              {!isNew && cfg.hasMaster && item.master && (
                item.master.locked ? (
                  <button data-testid="btn-unlock-master" onClick={unlockMaster} className="w-full border border-navy text-navy hover:bg-navy hover:text-white text-sm py-2 flex items-center justify-center gap-2"><Unlock size={14}/> Unlock master</button>
                ) : (
                  <div data-testid="master-unlocked-hint" className="text-[10px] mono uppercase tracking-widest text-[#D97706] text-center border border-[#D97706]/40 bg-[#D97706]/5 p-2">Master unlocked — update & save to commit</div>
                )
              )}
              {!isNew && cfg.requiresModeration && (
                item.moderation?.approved ? (
                  <button data-testid="btn-unapprove" onClick={unapproveTestimonial} className="w-full border border-stone2 text-stone2 hover:bg-stone2 hover:text-white text-sm py-2 flex items-center justify-center gap-2"><X size={14}/> Revoke approval</button>
                ) : (
                  <button data-testid="btn-approve-testimonial" onClick={approveTestimonial} className="w-full bg-[#3F6250] hover:bg-[#3F6250]/85 text-white text-sm py-2 flex items-center justify-center gap-2"><ShieldCheck size={14}/> Approve for publish</button>
                )
              )}
              {!isNew && (
                <button data-testid="btn-delete" onClick={del} className="w-full border border-[#9F1239] text-[#9F1239] hover:bg-[#9F1239] hover:text-white text-sm py-2 flex items-center justify-center gap-2"><Trash2 size={14}/> Delete</button>
              )}
            </div>
          </div>
          {!isNew && collName && <SeoAuditCard coll={collName} id={id}/>}

          {!isNew && (
            <div className="bg-white border border-line" data-testid="version-history">
              <button onClick={()=>setShowVersions(v=>!v)} className="w-full px-5 py-3 flex items-center justify-between text-left">
                <div className="flex items-center gap-2">
                  <History size={14} strokeWidth={1.5} className="text-prestige"/>
                  <div className="mono text-[10px] uppercase tracking-widest text-prestige">Version History</div>
                </div>
                <span className="mono text-[10px] text-stone2">{versions.length}</span>
              </button>
              {showVersions && (
                <div className="border-t border-line divide-y divide-line max-h-96 overflow-y-auto">
                  {versions.map(v => (
                    <div key={v.id} data-testid={`ver-${v.id}`} className="px-5 py-3">
                      <div className="mono text-xs text-navy">{v.created_at?.slice(0,16).replace('T',' ')}</div>
                      <div className="mono text-[10px] uppercase tracking-widest text-stone2 mt-0.5">by {v.created_by_name}</div>
                      <div className="text-xs text-stone2 mt-1 truncate">{v.snapshot?.title || v.snapshot?.name || v.snapshot?.slug || "—"}</div>
                      <button data-testid={`restore-${v.id}`} onClick={()=>restore(v.id)} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta mt-2 inline-flex items-center gap-1"><Check size={11}/> Restore this version</button>
                    </div>
                  ))}
                  {versions.length === 0 && <div className="p-4 text-xs text-stone2">No prior versions.</div>}
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}

function statusChip(s) {
  const m = {
    draft: "bg-line text-stone2",
    scheduled: "bg-[#D97706]/15 text-[#D97706]",
    published: "bg-[#3F6250]/10 text-[#3F6250]",
    unpublished: "bg-[#9F1239]/10 text-[#9F1239]",
  };
  return m[s] || m.draft;
}
function previewPath(type) {
  return { articles: "articles", conditions: "conditions", "research-updates": "research", "evidence-summaries": "research", guidelines: "research" }[type] || type;
}
function initFromSchema(sch) {
  const o = { status: "draft" };
  (sch||[]).forEach(([k, _, kind]) => {
    if (kind === "list" || kind === "tags") o[k] = [];
    else if (kind === "number") o[k] = 0;
    else if (kind === "checkbox") o[k] = false;
    else if (kind === "json") o[k] = [];
    else if (kind === "master") o[k] = { url: "", mime_type: "image/png", width_px: 0, height_px: 0, locked: false };
    else if (kind === "moderation") o[k] = { approved: false, review_notes: "" };
    else o[k] = "";
  });
  return o;
}

function FieldRow({ k, label, kind, required, value, onChange }) {
  const isSelect = kind.startsWith("select:");
  const opts = isSelect ? kind.slice(7).split("|") : [];
  const testid = `f-${k}`;
  if (isSelect) {
    return (
      <Field label={label} required={required}>
        <select data-testid={testid} value={value||""} onChange={e=>onChange(e.target.value)} className={inputCls}>
          {opts.map(o => <option key={o} value={o}>{o.replace(/_/g,' ')}</option>)}
        </select>
      </Field>
    );
  }
  if (kind === "textarea") return <Field label={label} required={required}><textarea data-testid={testid} rows={3} value={value||""} onChange={e=>onChange(e.target.value)} className={inputCls}/></Field>;
  if (kind === "longtext") return <Field label={label} required={required}><textarea data-testid={testid} rows={10} value={value||""} onChange={e=>onChange(e.target.value)} className={`${inputCls} font-mono text-xs`}/></Field>;
  if (kind === "list") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <Field label={label} required={required}>
        <textarea data-testid={testid} rows={4} placeholder="One item per line" value={arr.join("\n")} onChange={e=>onChange(e.target.value.split("\n").map(s=>s.trim()).filter(Boolean))} className={inputCls}/>
      </Field>
    );
  }
  if (kind === "tags") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <Field label={label} required={required}>
        <input data-testid={testid} placeholder="comma-separated" value={arr.join(", ")} onChange={e=>onChange(e.target.value.split(",").map(s=>s.trim()).filter(Boolean))} className={inputCls}/>
      </Field>
    );
  }
  if (kind === "number") return <Field label={label} required={required}><input data-testid={testid} type="number" value={value ?? ""} onChange={e=>onChange(Number(e.target.value)||0)} className={inputCls}/></Field>;
  if (kind === "checkbox") return <Field label={label} required={required}><label className="inline-flex items-center gap-2 text-sm text-navy"><input data-testid={testid} type="checkbox" checked={!!value} onChange={e=>onChange(e.target.checked)} className="h-4 w-4"/> {value ? "Yes" : "No"}</label></Field>;
  if (kind === "date") return <Field label={label} required={required}><input data-testid={testid} type="date" value={value||""} onChange={e=>onChange(e.target.value)} className={inputCls}/></Field>;
  if (kind === "datetime") return <Field label={label} required={required}><input data-testid={testid} type="datetime-local" value={value||""} onChange={e=>onChange(e.target.value)} className={inputCls}/></Field>;
  if (kind === "json") {
    const str = typeof value === "string" ? value : JSON.stringify(value ?? [], null, 2);
    return (
      <Field label={label} required={required}>
        <textarea data-testid={testid} rows={6} value={str}
          onChange={e => {
            const t = e.target.value;
            try { onChange(JSON.parse(t)); } catch { onChange(t); }
          }}
          className={`${inputCls} font-mono text-xs`}/>
        <div className="mono text-[9px] uppercase tracking-widest text-stone2 mt-1">Edit as JSON · invalid JSON is stored as string until fixed</div>
      </Field>
    );
  }
  if (kind === "master") return <MasterField k={k} value={value} onChange={onChange}/>;
  if (kind === "moderation") return <ModerationField value={value} onChange={onChange}/>;
  return <Field label={label} required={required}><input data-testid={testid} value={value||""} onChange={e=>onChange(e.target.value)} className={inputCls}/></Field>;
}

function MasterField({ k, value, onChange }) {
  // Default locked=false so a brand-new infographic can accept the master
  // asset on creation. Backend stamps locked=true on POST persist and
  // enforces immutability on subsequent PATCH.
  const v = value || { url: "", mime_type: "image/png", width_px: 0, height_px: 0, locked: false };
  const locked = v.locked === true;
  const set = (field, val) => onChange({ ...v, [field]: val });
  return (
    <div className="border border-line p-4 space-y-3" data-testid="master-field">
      <div className="flex items-center justify-between">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige">Master · original hi-res asset</div>
        <span data-testid="master-lock-badge" className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${locked ? "bg-[#9F1239]/10 text-[#9F1239]" : "bg-[#D97706]/15 text-[#D97706]"}`}>
          {locked ? "🔒 locked" : "🔓 unlocked"}
        </span>
      </div>
      {locked && (
        <div className="mono text-[10px] uppercase tracking-widest text-stone2 leading-relaxed">
          The master is preserved. Editors may add derivatives freely but the master can only be replaced after a Reviewer runs 'Unlock master' — the previous master is snapshotted to version history first.
        </div>
      )}
      <input data-testid="f-master-url" disabled={locked} value={v.url || ""} onChange={e => set("url", e.target.value)} placeholder="Master URL (SVG / hi-res PNG / PDF)" className={`${inputCls} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}/>
      <div className="grid grid-cols-2 gap-2">
        <input data-testid="f-master-mime" disabled={locked} value={v.mime_type || ""} onChange={e => set("mime_type", e.target.value)} placeholder="mime type" className={`${inputCls} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}/>
        <input data-testid="f-master-checksum" disabled={locked} value={v.checksum || ""} onChange={e => set("checksum", e.target.value)} placeholder="checksum (sha256)" className={`${inputCls} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}/>
        <input data-testid="f-master-w" disabled={locked} type="number" value={v.width_px || 0} onChange={e => set("width_px", Number(e.target.value) || 0)} placeholder="width px" className={`${inputCls} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}/>
        <input data-testid="f-master-h" disabled={locked} type="number" value={v.height_px || 0} onChange={e => set("height_px", Number(e.target.value) || 0)} placeholder="height px" className={`${inputCls} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}/>
      </div>
      <input data-testid="f-master-note" disabled={locked} value={v.note || ""} onChange={e => set("note", e.target.value)} placeholder="Provenance note (source, artist, tool)" className={`${inputCls} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}/>
    </div>
  );
}

function ModerationField({ value, onChange }) {
  const v = value || { approved: false, review_notes: "" };
  return (
    <div className="border border-line p-4 space-y-3" data-testid="moderation-field">
      <div className="flex items-center justify-between">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige">Moderation</div>
        <span data-testid="moderation-badge" className={`mono text-[10px] uppercase tracking-widest px-2 py-0.5 ${v.approved ? "bg-[#3F6250]/10 text-[#3F6250]" : "bg-[#D97706]/15 text-[#D97706]"}`}>
          {v.approved ? "approved" : "pending"}
        </span>
      </div>
      <div className="mono text-[10px] uppercase tracking-widest text-stone2 leading-relaxed">
        Testimonials cannot be published until moderation.approved is true AND consent_given is recorded. Use the Approve button on the right sidebar to record moderation.
      </div>
      {v.review_notes && <div className="text-xs text-navy italic">"{v.review_notes}"</div>}
      {v.approved_at && <div className="mono text-[9px] uppercase tracking-widest text-stone2">Approved at {v.approved_at}</div>}
    </div>
  );
}
function Field({ label, required, children }) {
  return (<label className="block"><div className="mono text-[10px] uppercase tracking-widest text-stone2 mb-1">{label}{required && <span className="text-[#9F1239]"> *</span>}</div>{children}</label>);
}


function SeoAuditCard({ coll, id }) {
  const [a, setA] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const run = () => {
    setLoading(true);
    seoApi.audit(coll, id).then(setA).catch(() => setA({ error: true })).finally(() => setLoading(false));
  };
  React.useEffect(run, [coll, id]);
  const supported = ["articles", "conditions", "guidelines", "faqs", "research", "research_papers",
                     "evidence_summaries", "videos", "patient_guides", "infographics", "books",
                     "testimonials", "media_appearances"].includes(coll);
  if (!supported) return null;
  return (
    <div className="bg-white border border-line p-5" data-testid="editor-seo-card">
      <div className="flex items-center justify-between mb-3">
        <div className="mono text-[10px] uppercase tracking-widest text-prestige">SEO checklist</div>
        <button onClick={run} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta">Re-run</button>
      </div>
      {loading || !a ? (
        <div className="text-xs text-stone2">Running…</div>
      ) : a.error ? (
        <div className="text-xs text-stone2">Save the item first to run the audit.</div>
      ) : (
        <>
          <div className="flex items-baseline justify-between mb-3">
            <div className={`font-serif text-3xl ${a.summary.publishable ? "text-[#3F6250]" : "text-[#D97706]"}`} data-testid="editor-seo-score">{a.summary.score_pct}%</div>
            <div className="mono text-[10px] uppercase tracking-widest text-stone2">{a.summary.pass}p·{a.summary.warn}w·{a.summary.fail}f</div>
          </div>
          <ul className="space-y-1 text-xs">
            {a.checks.slice(0, 6).map(c => (
              <li key={c.name} className="flex items-center gap-2">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.status === "pass" ? "bg-[#3F6250]" : c.status === "warn" ? "bg-[#D97706]" : c.status === "fail" ? "bg-[#9F1239]" : "bg-stone2"}`}></span>
                <span className="text-stone2 truncate">{c.name.replace(/_/g, " ")}</span>
              </li>
            ))}
          </ul>
          <a href={`/admin/seo?coll=${coll}&id=${id}`} className="mono text-[10px] uppercase tracking-widest text-navy hover:text-terracotta inline-block mt-3">View full audit →</a>
        </>
      )}
    </div>
  );
}
